import { useState } from 'react'
import { PublicLayout } from '@/components/layout/PublicLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { requestJson } from '@/lib/http'

type TaskRecord = {
  id: string
  status: string
  jurisdiction_code: string
  amount_cents: number
  currency: string
  reference_number?: string
  failure_reason?: string
}

type Props = {
  tasks: TaskRecord[]
}

export default function PlatformRemittanceTasksIndex({ tasks }: Props) {
  const [referenceByTask, setReferenceByTask] = useState<Record<string, string>>({})
  const [reasonByTask, setReasonByTask] = useState<Record<string, string>>({})
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const markRemitted = async (taskId: string) => {
    setMessage('')
    setError('')

    try {
      await requestJson(`/platform/business-registration/us/remittance_tasks/${taskId}`, {
        method: 'PATCH',
        data: {
          action: 'mark_remitted',
          reference_number: referenceByTask[taskId] || `REF-${Date.now()}`,
          evidence_notes: 'Marked as remitted by platform ops',
        },
      })
      setMessage('Remittance marked as completed')
      window.location.reload()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Request failed')
    }
  }

  const markFailed = async (taskId: string) => {
    setMessage('')
    setError('')

    try {
      await requestJson(`/platform/business-registration/us/remittance_tasks/${taskId}`, {
        method: 'PATCH',
        data: {
          action: 'mark_failed',
          reason: reasonByTask[taskId] || 'Manual remittance failed',
        },
      })
      setMessage('Remittance marked as failed')
      window.location.reload()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Request failed')
    }
  }

  return (
    <PublicLayout title="Remittance Tasks" fullWidth>
      <div className="p-8 max-w-5xl mx-auto space-y-4">
        <h1 className="text-2xl font-semibold">Remittance Operations Queue</h1>
        {message && <p className="text-sm text-green-700">{message}</p>}
        {error && <p className="text-sm text-red-700">{error}</p>}

        {tasks.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-sm text-muted-foreground">No remittance tasks pending.</CardContent>
          </Card>
        ) : (
          tasks.map((task) => (
            <Card key={task.id}>
              <CardHeader>
                <CardTitle className="text-base">
                  {task.jurisdiction_code} • {task.status} • {task.currency} {(task.amount_cents / 100).toFixed(2)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  placeholder="Reference number"
                  value={referenceByTask[task.id] || task.reference_number || ''}
                  onChange={(event) => setReferenceByTask({ ...referenceByTask, [task.id]: event.target.value })}
                />
                <Input
                  placeholder="Failure reason"
                  value={reasonByTask[task.id] || task.failure_reason || ''}
                  onChange={(event) => setReasonByTask({ ...reasonByTask, [task.id]: event.target.value })}
                />
                <div className="flex gap-2">
                  <Button onClick={() => markRemitted(task.id)}>Mark Remitted</Button>
                  <Button variant="outline" onClick={() => markFailed(task.id)}>Mark Failed</Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </PublicLayout>
  )
}
